import React, { useEffect, useState } from "react";
import { Select } from "antd";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { getClinicUsers } from "../../../../apis/user";
import { getProcedures } from "../../../../apis/procedures";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const MINUTE_OPTIONS = ["00", "15", "30", "45"];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));

interface ClinicUser {
  id: number;
  firstName?: string;
  first_name?: string;
  lastName?: string;
  last_name?: string;
  roles?: string[];
}

interface ProcedureRow {
  id: number | null;
  name: string;
}

interface OTFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const userName = (u: ClinicUser) =>
  `${u.firstName ?? u.first_name ?? ""} ${u.lastName ?? u.last_name ?? ""}`.trim() ||
  `User ${u.id}`;

const parseProcedures = (initialData?: any): ProcedureRow[] => {
  // New format: procedures[]
  if (Array.isArray(initialData?.procedures) && initialData.procedures.length > 0) {
    return initialData.procedures.map((p: any) =>
      typeof p === "string"
        ? { id: null, name: p }
        : { id: p.id ?? null, name: p.name ?? "" }
    );
  }
  // Backward compat: old single procedureName field
  if (initialData?.procedureName) {
    return [{ id: null, name: initialData.procedureName }];
  }
  return [{ id: null, name: "" }];
};

const parseTime = (time?: string): { h: string; m: string } => {
  if (!time) return { h: "", m: "00" };
  const [h = "", rawM = "00"] = time.split(":");
  // Snap minute to nearest 15
  const mNum = parseInt(rawM, 10) || 0;
  const snapped = [0, 15, 30, 45].reduce((best, v) =>
    Math.abs(v - mNum) < Math.abs(best - mNum) ? v : best
  , 0);
  return { h, m: String(snapped).padStart(2, "0") };
};

const OTForm: React.FC<OTFormProps> = ({ initialData, onSave, onCancel }) => {
  const [doctors, setDoctors] = useState<ClinicUser[]>([]);
  const [procedureMaster, setProcedureMaster] = useState<{ value: number; label: string }[]>([]);
  const [procedures, setProcedures] = useState<ProcedureRow[]>(parseProcedures(initialData));
  const [timeH, setTimeH] = useState(parseTime(initialData?.procedureTime).h);
  const [timeM, setTimeM] = useState(parseTime(initialData?.procedureTime).m);
  const [formData, setFormData] = useState({
    procedureDate: initialData?.procedureDate || "",
    status: initialData?.status || "scheduled",
    surgeon1: (initialData?.surgeon1 ?? null) as number | null,
    surgeon2: (initialData?.surgeon2 ?? null) as number | null,
    anaesthetists: (initialData?.anaesthetists ?? null) as number | null,
    assistants: (initialData?.assistants ?? null) as number | null,
    findings: initialData?.findings || "",
    bloodLossMl: initialData?.bloodLossMl || "",
    complications: initialData?.complications || "",
    implants: initialData?.implants || "",
    notes: initialData?.notes || "",
  });
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  useEffect(() => {
    getClinicUsers().then((res: any) => {
      const raw = res?.data ?? res;
      const data: ClinicUser[] = Array.isArray(raw) ? raw : raw?.data ?? [];
      // Filter to doctors only
      setDoctors(data.filter((u) => Array.isArray(u.roles) && u.roles.includes("doctor")));
    });

    getProcedures({ per_page: 500, active_only: true }).then((res: any) => {
      const raw = res?.data ?? res;
      const list: any[] = Array.isArray(raw) ? raw : raw?.data ?? [];
      setProcedureMaster(list.map((p) => ({ value: p.id, label: p.name })));
    });
  }, []);

  useEffect(() => {
    const raw = initialData?.operationDetails;
    if (raw) {
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        setEditorState(EditorState.createWithContent(convertFromRaw(parsed)));
      } catch {
        setEditorState(
          EditorState.createWithContent(ContentState.createFromText(String(raw)))
        );
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  }, [initialData?.operationDetails]);

  const addProcedureRow = () =>
    setProcedures((prev) => [...prev, { id: null, name: "" }]);

  const removeProcedureRow = (idx: number) =>
    setProcedures((prev) => prev.filter((_, i) => i !== idx));

  const updateProcedureRow = (idx: number, id: number | null, name: string) =>
    setProcedures((prev) =>
      prev.map((p, i) => (i === idx ? { id, name } : p))
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const operationDetails = JSON.stringify(convertToRaw(contentState));
    const procedureTime = timeH ? `${timeH}:${timeM}` : "";
    onSave({
      ...formData,
      procedureTime,
      procedures: procedures.filter((p) => p.name.trim()),
      // backward-compat: primary procedure name
      procedureName: procedures[0]?.name ?? "",
      operationDetails,
    });
  };

  const doctorOptions = doctors.map((u) => ({ value: u.id, label: userName(u) }));

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-5">

      {/* ── Procedures ── */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="block text-sm font-medium text-gray-700">
            Procedure(s) <span className="text-red-500">*</span>
          </label>
          <button
            type="button"
            onClick={addProcedureRow}
            className="text-xs font-medium text-primary hover:underline"
          >
            + Add another procedure
          </button>
        </div>

        <div className="space-y-2">
          {procedures.map((row, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <div className="flex-1">
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  placeholder="Select or search procedure…"
                  value={row.id ?? undefined}
                  onChange={(val, opt: any) => {
                    updateProcedureRow(idx, val, opt?.label ?? "");
                  }}
                  filterOption={(input, option) =>
                    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  options={procedureMaster}
                  allowClear
                  onClear={() => updateProcedureRow(idx, null, "")}
                  notFoundContent={
                    <span className="text-xs text-gray-400">
                      Not listed? Add it in Masters → Procedures.
                    </span>
                  }
                />
              </div>
              {procedures.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeProcedureRow(idx)}
                  className="text-gray-400 hover:text-red-500 text-lg leading-none px-1"
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Date + Time ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            value={formData.procedureDate}
            onChange={(e) => setFormData({ ...formData, procedureDate: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Time</label>
          <div className="mt-1 flex items-center gap-1">
            <select
              value={timeH}
              onChange={(e) => setTimeH(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">HH</option>
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
            <span className="text-gray-500 font-medium">:</span>
            <select
              value={timeM}
              onChange={(e) => setTimeM(e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-2 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {MINUTE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Status ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <Select
          style={{ width: "100%" }}
          value={formData.status}
          onChange={(v) => setFormData({ ...formData, status: v })}
          options={STATUS_OPTIONS}
        />
      </div>

      {/* ── Surgeons ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Surgeon 1</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select primary surgeon"
            value={formData.surgeon1 ?? undefined}
            onChange={(v) => setFormData({ ...formData, surgeon1: v ?? null })}
            allowClear
            showSearch
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={doctorOptions}
            className="mt-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Surgeon 2</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select secondary surgeon"
            value={formData.surgeon2 ?? undefined}
            onChange={(v) => setFormData({ ...formData, surgeon2: v ?? null })}
            allowClear
            showSearch
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={doctorOptions}
            className="mt-1"
          />
        </div>
      </div>

      {/* ── Anaesthetist + Assistant ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Anaesthetist</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select anaesthetist"
            value={formData.anaesthetists ?? undefined}
            onChange={(v) => setFormData({ ...formData, anaesthetists: v ?? null })}
            allowClear
            showSearch
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={doctorOptions}
            className="mt-1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Assistant</label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select assistant"
            value={formData.assistants ?? undefined}
            onChange={(v) => setFormData({ ...formData, assistants: v ?? null })}
            allowClear
            showSearch
            filterOption={(input, opt) =>
              String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={doctorOptions}
            className="mt-1"
          />
        </div>
      </div>

      {/* ── Operation Details ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Operation Details</label>
        <Editor
          editorState={editorState}
          onEditorStateChange={setEditorState}
          toolbarClassName="border border-gray-300 rounded-t-md"
          wrapperClassName="border border-gray-300 rounded-md"
          editorClassName="p-3 min-h-[160px] border-t border-gray-300"
          toolbar={{
            options: ["inline", "list", "blockType"],
            inline: { options: ["bold", "italic", "underline"] },
          }}
        />
      </div>

      {/* ── Clinical fields ── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Findings</label>
          <textarea
            value={formData.findings}
            onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Blood Loss (ml)</label>
          <input
            type="number"
            min={0}
            value={formData.bloodLossMl}
            onChange={(e) => setFormData({ ...formData, bloodLossMl: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Complications</label>
          <textarea
            value={formData.complications}
            onChange={(e) => setFormData({ ...formData, complications: e.target.value })}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Implants</label>
          <textarea
            value={formData.implants}
            onChange={(e) => setFormData({ ...formData, implants: e.target.value })}
            rows={2}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notes</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90"
        >
          Save OT
        </button>
      </div>
    </form>
  );
};

export default OTForm;
